import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ClipboardList, Filter } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { formatDateTimeForLocale, useOrganizationDateLocale } from '@/lib/org-date-locale'
import {
  financeApi,
  type NotificationDeliveryLogRow,
  type NotificationEventOption,
} from '@/services/finance'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
] as const

const TRIGGER_LABELS: Record<string, string> = {
  periodic_celery: 'Celery (scheduled)',
  manual_generate: 'Manual generate',
  invoices_once: 'Celery (invoices-once)',
}

const STATUS_BADGE: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  sent: 'default',
  failed: 'destructive',
  skipped: 'secondary',
}

type SettlementNotificationDeliveryLogPanelProps = {
  serviceInstanceId?: string | null
  runIdFilter?: string | null
  eventOptions?: NotificationEventOption[]
  embedded?: boolean
}

function formatDateTime(value?: string | null, locale?: string) {
  if (!value) return '—'
  return formatDateTimeForLocale(value, locale)
}

function shortRunId(runId?: string | null) {
  if (!runId) return '—'
  if (runId.length <= 12) return runId
  return `${runId.slice(0, 8)}…${runId.slice(-4)}`
}

function invoiceRef(row: NotificationDeliveryLogRow) {
  if (row.settlement_invoice_id) return `Settlement #${row.settlement_invoice_id}`
  if (row.worker_payment_invoice_id) return `Worker COD #${row.worker_payment_invoice_id}`
  return '—'
}

export function SettlementNotificationDeliveryLogPanel({
  serviceInstanceId,
  runIdFilter,
  eventOptions = [],
  embedded = false,
}: SettlementNotificationDeliveryLogPanelProps) {
  const organizationDateLocale = useOrganizationDateLocale(serviceInstanceId)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [statusFilter, setStatusFilter] = useState('all')
  const [eventFilter, setEventFilter] = useState('all')
  const [runFilter, setRunFilter] = useState(runIdFilter ?? '')

  useEffect(() => {
    if (runIdFilter) setRunFilter(runIdFilter)
  }, [runIdFilter])

  const eventsQuery = useQuery({
    queryKey: ['notification-events'],
    queryFn: () => financeApi.getNotificationEvents(),
    enabled: eventOptions.length === 0,
  })

  const resolvedEventOptions = useMemo(() => {
    if (eventOptions.length > 0) return eventOptions
    return (eventsQuery.data?.list ?? []) as NotificationEventOption[]
  }, [eventOptions, eventsQuery.data])

  const effectiveRunId = runFilter.trim() || undefined

  const logsQuery = useQuery({
    queryKey: [
      'notification-delivery-logs',
      serviceInstanceId,
      page,
      pageSize,
      statusFilter,
      eventFilter,
      effectiveRunId,
    ],
    queryFn: () =>
      financeApi.getNotificationDeliveryLogs({
        service_instance_id: serviceInstanceId ?? undefined,
        page,
        page_size: pageSize,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        notification_key: eventFilter !== 'all' ? eventFilter : undefined,
        run_id: effectiveRunId,
      }),
    enabled: Boolean(serviceInstanceId),
  })

  const response = logsQuery.data ?? { count: 0, results: [] }
  const rows = response.results ?? []

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        if (row.status === 'sent') acc.sent += 1
        else if (row.status === 'failed') acc.failed += 1
        else if (row.status === 'skipped') acc.skipped += 1
        return acc
      },
      { sent: 0, failed: 0, skipped: 0 },
    )
  }, [rows])

  if (!serviceInstanceId) {
    return null
  }

  return (
    <Card className={embedded ? 'border-border/70 shadow-none' : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {!embedded ? <ClipboardList className="h-4 w-4" /> : null}
          Notification delivery log
        </CardTitle>
        <CardDescription>
          Delivery attempts for contacts on this service instance only (email, SMS, WhatsApp, push). Counterparty sends
          appear in their instance&apos;s log.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <span>
            Sent (page): <strong className="text-foreground">{summary.sent}</strong>
          </span>
          <span>
            Failed (page): <strong className="text-destructive">{summary.failed}</strong>
          </span>
          <span>
            Skipped (page): <strong className="text-foreground">{summary.skipped}</strong>
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <Filter className="mb-2 h-4 w-4 text-muted-foreground" />
          <div className="grid gap-1.5">
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs text-muted-foreground">Event</Label>
            <Select
              value={eventFilter}
              onValueChange={(value) => {
                setEventFilter(value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="All events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All events</SelectItem>
                {resolvedEventOptions.map((event) => (
                  <SelectItem key={event.key} value={event.key}>
                    {event.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {runIdFilter ? (
            <div className="grid gap-1.5">
              <Label className="text-xs text-muted-foreground">Run ID</Label>
              <Select
                value={runFilter ? 'filtered' : 'all'}
                onValueChange={(value) => {
                  if (value === 'all') setRunFilter('')
                  else setRunFilter(runIdFilter)
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All runs</SelectItem>
                  <SelectItem value="filtered">Latest generate ({shortRunId(runIdFilter)})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>

        {logsQuery.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border/70 p-6 text-sm text-muted-foreground">
            No notification delivery records yet. Logs appear after Celery invoice generation or manual period
            generate when contacts are configured.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-border/70">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run at</TableHead>
                    <TableHead>Run ID</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Sent at</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {formatDateTime(row.run_at, organizationDateLocale)}
                      </TableCell>
                      <TableCell className="font-mono text-xs" title={row.run_id}>
                        {shortRunId(row.run_id)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {TRIGGER_LABELS[row.trigger_source] ?? row.trigger_source}
                      </TableCell>
                      <TableCell className="text-xs">{row.notification_label ?? row.notification_key}</TableCell>
                      <TableCell className="text-xs">
                        {row.contact_label?.trim() || (row.notification_contact_id ? `#${row.notification_contact_id}` : '—')}
                      </TableCell>
                      <TableCell className="capitalize text-xs">{row.channel}</TableCell>
                      <TableCell className="max-w-[160px] truncate text-xs" title={row.recipient}>
                        {row.recipient || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE[row.status] ?? 'outline'} className="capitalize">
                          {row.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{invoiceRef(row)}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {formatDateTime(row.sent_at, organizationDateLocale)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-xs text-destructive" title={row.error_message}>
                        {row.error_message || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DataTablePagination
              page={page}
              pageSize={pageSize}
              totalCount={response.count ?? 0}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next)
                setPage(1)
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
